import React, {FC, memo, useEffect, useState} from 'react';
import {
    ICategoryProductFilterPage,
    IM19ShopTheLookBundleCardBlock,
    IProductFilterPage,
    IProductListVariant,
    IProfessionalProductList,
    IRelatedCategory
} from "@montana/models/generated/cms.interface";
import {Container} from '@montana/shared/container';
import {useFilter} from '@montana/containers/filter';
import {FilterControls} from './filter-controls.component';
import {Button, ButtonStyles} from '@montana/shared/button';
import {Translate} from '@montana/shared/translate';
import {formatString} from '@montana/helpers/string-format/string-format.helper';
import {useRouter} from '@montana/containers/router';
import {ProductCard} from '@montana/shared/product-card';
import {ProductCardProfessional} from '@montana/shared/product-card-professional';
import {DynamicBlocks} from '@montana/blocks/dynamic-blocks.component';
import {useTracking} from '@montana/containers/tracking';
import {Spinner} from '@montana/shared/spinner/spinner.component';
import {useTranslations} from '@montana/containers/translations';
import {urlWithoutMarket} from '@montana/helpers/market.helper';
import {calculateNextPageSize} from '@montana/helpers/filter.helper';
import {useSettings} from '@montana/containers/settings/settings.container';
import {CUSTOMER_MODE} from '@montana/constants/customer-mode';
import {getColorSwatchesById} from "@montana/helpers/color.helper";
import {useDynamicSettings} from "@montana/containers/dynamic-settings";

export const FilterPage: FC<IProductFilterPage | ICategoryProductFilterPage> = memo((props: IProductFilterPage | ICategoryProductFilterPage): JSX.Element => {
    const {
        relatedCategories,
        topBlocks,
        bottomBlocks,
    } = props;
    const { location } = useRouter();
    const { loadMore, isLoadingMore, hits, meta, isFetchingFilter, facets } = useFilter();
    const {customerMode} = useSettings();

    const mergeFilterHitsWithContentCards = () => {
        // If we are on a product filter page we can expect content cards to be thrown in the mix. Commence shuffling.
        const { contentCards, contentCardInterval } = props;

        if (contentCards?.length && hits?.length) {
            const interval = contentCardInterval || 10; // We do need an interval, so if there is none provided we default to 10.
            const allowedContentCardSlots = Math.floor(hits.length / interval);
            const expectedMergedLength = hits.length + (allowedContentCardSlots > contentCards.length ? contentCards.length : allowedContentCardSlots);

            const entries: (IProductListVariant | IProfessionalProductList | IM19ShopTheLookBundleCardBlock)[] = [];
            let hitIndex = 0;
            let contentIndex = 0;
            for (let i = 1; i < expectedMergedLength + 1; i++) {
                if ((i % interval) === 0 && contentIndex < contentCards.length) {
                    entries.push(contentCards[contentIndex++]);
                } else {
                    entries.push(hits[hitIndex++]);
                }
            }

            return entries;
        } else {
            // If there is no contentcards, we simply show the hits.
            return hits;
        }
    }

    const {colors = []} = useDynamicSettings();
    const [filterEntries, setFilterEntries] = useState<(IProductListVariant | IProfessionalProductList | IM19ShopTheLookBundleCardBlock)[] | undefined>(mergeFilterHitsWithContentCards());
    const translate = useTranslations();
    const { trackProductImpression } = useTracking();

    useEffect(() => {
        if (!!hits?.length) {
            trackProductImpression(hits, 'category');
        }
        setFilterEntries(mergeFilterHitsWithContentCards());
    }, [hits]);

    const isActiveCategory = (category: IRelatedCategory) => {
        if (!category.link) {
            return false;
        }
        return urlWithoutMarket(category.link) === urlWithoutMarket(location.pathname);
    };

    return (
        <Container>
            <DynamicBlocks blocks={topBlocks} />
            <>
                {!!relatedCategories?.length && (
                    <section className="filter-page__related-categories">
                        {relatedCategories?.map((category, index) => (
                            <Button
                                key={index}
                                className={`filter-page__related-category${category.isParent ? ' filter-page__related-category--parent' : ''}`}
                                style={isActiveCategory(category) ? ButtonStyles.Tertiary : ButtonStyles.Secondary}
                                title={category.name}
                                href={category.link}
                            >
                                {category.name}
                            </Button>
                        ))}
                    </section>
                )}

                {isFetchingFilter && (
                    <div className="filter-page__product-grid-placeholder">
                        <Spinner />
                    </div>
                )}
                {!isFetchingFilter && !!facets?.length && (
                    <FilterControls />
                )}
                {!isFetchingFilter && !hits?.length && (
                    <div className="filter-page__product-grid-placeholder">
                        <p>{translate(t => t.filterAndSearch.noFilterResults, `[Sorry - we could not find any results for the selected filter.]`)}</p>
                    </div>
                )}
                {!isFetchingFilter && !!hits?.length && meta && (
                    <>
                        <section className="filter-page__product-grid">
                            {filterEntries?.map((p: IProductListVariant | IProfessionalProductList | IM19ShopTheLookBundleCardBlock, index: number) => {
                                if (!!p) {
                                    if (p?.hasOwnProperty('isComposition')) { // isComposition will tell us if this is content or er variant card.
                                        const contentEntry = p as IM19ShopTheLookBundleCardBlock;
                                        return (
                                            <ProductCard
                                                key={`${contentEntry.epiId}-${index}`}
                                                customImage={contentEntry.image}
                                                link={{
                                                    url: contentEntry.link?.url,
                                                    title: contentEntry.link?.title
                                                }}
                                                overrideInformation={(
                                                    <div className="filter-page__content-card-information">
                                                        <div>
                                                            <p className="filter-page__content-card-title">{contentEntry.title}</p>
                                                            {!!contentEntry.isComposition && !!contentEntry.price?.priceWithDiscount && <p className="filter-page__content-card-price">{contentEntry.price?.priceWithDiscount.formattedWithCurrency}</p>}
                                                            {!contentEntry.isComposition && !!contentEntry.cheapestPrice && <p className="filter-page__content-card-price">{contentEntry.cheapestPrice?.formattedWithCurrency}</p>}
                                                        </div>
                                                        <Button
                                                            className="filter-page__content-card-cta"
                                                            text={contentEntry.link?.title || ''}
                                                            style={ButtonStyles.Tertiary}
                                                            title={contentEntry.link?.title}
                                                            tabIndex={-1}
                                                        />
                                                    </div>
                                                )}
                                                sizes={[
                                                    '(min-width: 1680px) 300px',
                                                    '(min-width: 1367px) 220px',
                                                    '(min-width: 811px) 30vw',
                                                    '(min-width: 480px) 50vw',
                                                    '80vw'
                                                ].join(', ')}
                                            />
                                        )
                                    } else {
                                        if (customerMode === CUSTOMER_MODE.PRIVATE || customerMode === CUSTOMER_MODE.B2B) {
                                            const productEntry = p as IProductListVariant;
                                            return (
                                                <ProductCard
                                                    key={`${productEntry.code}-${index}`}
                                                    title={productEntry.displayName}
                                                    // imageUrl={productEntry.productImageViewModel?.imageRenderingMethod === LondonDynamicsImageRendering ? productEntry.productImageViewModel?.londonDynamicsStaticImageUrl : productEntry.productImageViewModel?.cylindoImageUrl}
                                                    imageUrl={productEntry.productImageViewModel?.londonDynamicsStaticImageUrl}
                                                    customImage={productEntry.productImageViewModel?.useAssetImage ? productEntry.productImageViewModel?.assetImageViewModel : undefined}
                                                    dimensions={productEntry.dimensions}
                                                    price={productEntry.price}
                                                    link={{ url: productEntry.productPath, title: productEntry.displayName }}
                                                    gtmProduct={{
                                                        id: productEntry.code || '',
                                                        name: productEntry.displayName || '',
                                                        list: '',
                                                        position: index + 1,
                                                        price: productEntry.price?.priceWithDiscount?.amount || 0,
                                                    }}
                                                    sizes={[
                                                        '(min-width: 1680px) 300px',
                                                        '(min-width: 1367px) 220px',
                                                        '(min-width: 811px) 30vw',
                                                        '(min-width: 480px) 50vw',
                                                        '80vw'
                                                    ].join(', ')}
                                                    numberOfVariantColours={productEntry.numberOfVariantColours}
                                                    colourSwatches={getColorSwatchesById(productEntry.variantColours, colors)}
                                                />
                                            )
                                        }

                                        if (customerMode === CUSTOMER_MODE.PROFESSIONAL) {
                                            const productEntry = p as IProfessionalProductList;
                                            return (
                                                <ProductCardProfessional
                                                    key={`${productEntry.productTitleHeader}-${index}`}
                                                    description={productEntry.productPLPDescription}
                                                    download3DFile={productEntry.productModel3DFile}
                                                    downloadSheet={productEntry.productSheet}
                                                    fromPrice={productEntry.productFromPrice}
                                                    imageUrl={productEntry.productRessourcePackShot?.url}
                                                    moreInformation={productEntry.productMoreInformationPDPLink}
                                                    title={productEntry.productTitleHeader}
                                                    gtmProduct={{
                                                        id: '',
                                                        name: productEntry.productTitleHeader || '',
                                                        list: '',
                                                        position: index + 1,
                                                        price: productEntry.productFromPrice?.amount || 0,
                                                    }}
                                                />
                                            )
                                        }
                                    }
                                }
                            })}
                        </section>

                        <section className="filter-page__pagination">
                            <span className="filter-page__pagination-text">
                                <Translate value={(t): string => t.filterAndSearch.itemsShowingOutOfTotal} defaultValue="Showing {0} out of {1} items">
                                    {(t): string => formatString(t, hits.length, meta?.hits)}
                                </Translate>
                            </span>
                            {calculateNextPageSize(hits.length || 0, meta) > 0 && (
                                <div className="filter-page__pagination-button-container">
                                    <Button
                                        className="filter-page__pagination-button"
                                        isLoading={isLoadingMore}
                                        onClick={() => loadMore(customerMode)}
                                        style={ButtonStyles.Tertiary}
                                    >
                                        <Translate value={(t): string => t.filterAndSearch.loadMore} defaultValue="Load more ({0})">
                                            {(t): string => formatString(t, calculateNextPageSize(hits.length, meta))}
                                        </Translate>
                                    </Button>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </>
            <DynamicBlocks blocks={bottomBlocks} />
        </Container>
    );
});
